/**
 * Spreadsheet grid — the shared, read-only CSV table view.
 *
 * A scrollable table with a pinned header row and a pinned row-number
 * column; numeric-looking cells get `tabular-nums` + right alignment so
 * columns of figures line up at the decimal point the way they would in
 * Excel. Empty cells render a muted em dash and blank headers fall back to
 * a `col N` placeholder.
 *
 * This is the single component both surfaces render `kind: "spreadsheet"`
 * artifacts through:
 *  - the chat artifact canvas ({@link SpreadsheetCanvas}) wraps it in a
 *    `relative` stage and passes `absolute inset-0` so it fills the pane;
 *  - the Library file-preview ({@link SpreadsheetRenderer}) passes `h-full`
 *    so opening a spreadsheet from the Library is pixel-identical to chat.
 *
 * Callers own the parse (both use `parseCsv` from `@/lib/artifacts/csv`) and
 * the surrounding chrome; this component owns only the table + its scroll
 * container.
 *
 * @module components/artifacts/spreadsheet-grid
 */

import type { ParsedCsv } from "@/lib/artifacts/csv";
import { looksNumeric } from "@/lib/artifacts/csv";
import { cn } from "@/lib/utils";

export interface SpreadsheetGridProps {
  /** Parsed CSV body — headers + rows. */
  parsed: ParsedCsv;
  /**
   * Applied to the scroll container. Callers use it to position/size the
   * grid for their layout (`absolute inset-0` in the chat stage, `h-full`
   * in the Library dialog) and to set a background.
   */
  className?: string;
}

/**
 * Scrollable spreadsheet table with sticky header + row-number column.
 * See the module doc for the chat vs. Library split.
 */
export function SpreadsheetGrid({ parsed, className }: SpreadsheetGridProps) {
  return (
    <div className={cn("overflow-auto", className)}>
      <table className="w-full border-collapse text-sm">
        <thead className="sticky top-0 z-10 bg-muted text-muted-foreground">
          <tr>
            <th className="sticky left-0 z-20 border-r border-b border-border bg-muted px-2 py-1.5 text-right text-xs font-medium tabular-nums w-12">
              #
            </th>
            {parsed.headers.map((header, idx) => (
              <th
                key={`${header}-${idx}`}
                className="border-r border-b border-border bg-muted px-3 py-2 text-left font-semibold whitespace-nowrap"
                title={header}
              >
                {header || (
                  <span className="text-muted-foreground/60">col {idx + 1}</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {parsed.rows.map((row, rowIdx) => (
            <tr key={rowIdx} className="hover:bg-muted/30 transition-colors">
              <td className="sticky left-0 z-[5] border-r border-b border-border bg-muted/40 px-2 py-1.5 text-right text-xs text-muted-foreground tabular-nums w-12">
                {rowIdx + 1}
              </td>
              {parsed.headers.map((_, colIdx) => {
                const value = row[colIdx] ?? "";
                const numeric = looksNumeric(value);
                return (
                  <td
                    key={colIdx}
                    className={cn(
                      "border-r border-b border-border px-3 py-1.5 align-top",
                      numeric && "text-right tabular-nums",
                      !value && "text-muted-foreground/40",
                    )}
                  >
                    {value || "—"}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
